import { WritableSignal, signal } from '@angular/core';

import { PaginationConfig } from '@shared/components/pagination/pagination.component';

export interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onlySelected?: boolean;
  selectedItemsCount?: number;
}

export class Pagination {
  currentPage: WritableSignal<number>;
  itemsPerPage: WritableSignal<number>;
  totalItems: WritableSignal<number>;

  constructor(currentPage: number = 1, itemsPerPage: number = 25, totalItems: number = 0) {
    this.currentPage = signal(currentPage);
    this.itemsPerPage = signal(itemsPerPage);
    this.totalItems = signal(totalItems);
  }

  getTotalPages(): number {
    return Pagination.getTotalPages(this.totalItems(), this.itemsPerPage());
  }

  normalizePage(page?: number): number {
    const totalPages = this.getTotalPages();
    const pageToNormalize = page ?? this.currentPage();
    return Pagination.normalizePage(pageToNormalize, totalPages);
  }

  setPage(page: number): void {
    const normalizedPage = this.normalizePage(page);
    this.currentPage.set(normalizedPage);
  }

  resetToFirstPage(): void {
    this.currentPage.set(1);
  }

  shouldShowPagination(): boolean {
    return this.getTotalPages() > 1;
  }

  createPaginationConfig(state?: PaginationState): PaginationConfig {
    const effectiveState = state ?? {
      currentPage: this.currentPage(),
      itemsPerPage: this.itemsPerPage(),
      totalItems: this.totalItems()
    };
    return Pagination.createPaginationConfig(effectiveState);
  }

  updateTotalItems(total: number): void {
    this.totalItems.set(total);
    const normalizedPage = this.normalizePage();
    if (normalizedPage !== this.currentPage()) {
      this.currentPage.set(normalizedPage);
    }
  }

  handleRowSelection<T>(
    row: T,
    getItemId: (item: T) => string,
    currentSelection: Set<string>,
    callbacks: {
      updateSelection: (selection: Set<string>) => void;
      updateOnlySelected?: (value: boolean) => void;
      onSelectionChange?: () => void;
    }
  ): void {
    const itemId = getItemId(row);
    const newSelection = new Set(currentSelection);

    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      newSelection.add(itemId);
    }

    if (newSelection.size === 0 && callbacks.updateOnlySelected) {
      callbacks.updateOnlySelected(false);
    }

    const sizeCurrentSelection = Math.ceil(newSelection.size / this.itemsPerPage());
    if (sizeCurrentSelection < this.currentPage()) {
      this.setPage(sizeCurrentSelection);
    }

    callbacks.updateSelection(newSelection);
    callbacks.onSelectionChange?.();
  }

  handleSearch(searchTerm: string, updateSearchCallback: (term: string) => void): void {
    updateSearchCallback(searchTerm);
    this.resetToFirstPage();
  }

  handleOnlySelectedToggle(
    onlySelected: boolean,
    updateOnlySelectedCallback: (value: boolean) => void
  ): void {
    updateOnlySelectedCallback(onlySelected);
    this.resetToFirstPage();
  }

  processListPagination<T>(
    allItems: T[],
    selectedItemIds: Set<string>,
    getItemId: (item: T) => string,
    onlySelected: boolean
  ): {
    displayedItems: T[];
    paginationConfig: PaginationConfig;
    shouldShowPagination: boolean;
  } {
    const state: PaginationState = {
      currentPage: this.currentPage(),
      itemsPerPage: this.itemsPerPage(),
      totalItems: this.totalItems(),
      onlySelected,
      selectedItemsCount: selectedItemIds.size
    };

    return Pagination.processListPagination(allItems, selectedItemIds, state, getItemId);
  }

  static getTotalPages(totalItems: number, itemsPerPage: number): number {
    if (itemsPerPage <= 0) return 1;
    if (isNaN(totalItems) || isNaN(itemsPerPage)) return 1;
    return Math.ceil(totalItems / itemsPerPage);
  }

  static normalizePage(page: number, totalPages: number): number {
    if (totalPages <= 0) return 1;
    return Math.max(1, Math.min(page, totalPages));
  }

  static sliceForPage<T>(items: T[], currentPage: number, itemsPerPage: number): T[] {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = currentPage * itemsPerPage;
    return items.slice(startIndex, endIndex);
  }

  static createPaginationConfig(state: PaginationState): PaginationConfig {
    const { currentPage, itemsPerPage, totalItems, onlySelected, selectedItemsCount } =
      state;

    const effectiveTotalItems =
      onlySelected && selectedItemsCount !== undefined ? selectedItemsCount : totalItems;

    const totalPages = this.getTotalPages(effectiveTotalItems, itemsPerPage);
    const normalizedCurrentPage = this.normalizePage(currentPage, totalPages);

    return {
      currentPage: normalizedCurrentPage,
      totalPages
    };
  }

  static shouldShowPagination(state: PaginationState): boolean {
    const config = this.createPaginationConfig(state);
    return config.totalPages > 1;
  }

  static processListPagination<T>(
    allItems: T[],
    selectedItemIds: Set<string>,
    state: PaginationState,
    getItemId: (item: T) => string
  ): {
    displayedItems: T[];
    paginationConfig: PaginationConfig;
    shouldShowPagination: boolean;
  } {
    const { onlySelected, currentPage, itemsPerPage } = state;

    let displayedItems: T[];
    let paginationState: PaginationState;

    if (onlySelected && selectedItemIds.size > 0) {
      const selectedItems = allItems.filter((item) => selectedItemIds.has(getItemId(item)));
      displayedItems = this.sliceForPage(selectedItems, currentPage, itemsPerPage);

      paginationState = {
        ...state,
        selectedItemsCount: selectedItemIds.size
      };
    } else {
      displayedItems = allItems;
      paginationState = state;
    }

    const paginationConfig = this.createPaginationConfig(paginationState);
    const shouldShowPagination = this.shouldShowPagination(paginationState);

    return {
      displayedItems,
      paginationConfig,
      shouldShowPagination
    };
  }

  static handleSearch(
    searchTerm: string,
    updateSearchCallback: (term: string) => void,
    resetPageCallback: () => void
  ): void {
    updateSearchCallback(searchTerm);
    resetPageCallback();
  }

  static handleOnlySelectedToggle(
    onlySelected: boolean,
    updateOnlySelectedCallback: (value: boolean) => void,
    resetPageCallback: () => void
  ): void {
    updateOnlySelectedCallback(onlySelected);
    resetPageCallback();
  }

  static handleRowSelection<T>(
    row: T,
    getItemId: (item: T) => string,
    currentSelection: Set<string>,
    callbacks: {
      updateSelection: (selection: Set<string>) => void;
      updateOnlySelected?: (value: boolean) => void;
    }
  ): void {
    const itemId = getItemId(row);
    const newSelection = new Set(currentSelection);

    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      newSelection.add(itemId);
    }
    if (newSelection.size === 0 && callbacks.updateOnlySelected) {
      callbacks.updateOnlySelected(false);
    }
    callbacks.updateSelection(newSelection);
  }
}
